import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Icon } from '../../icons';
import CloverLogo from '../../components/ui/CloverLogo';
import { createSociety } from '../../api/onboarding';
import { Modal, Btn } from '../../components/ui';
import { sendVerificationOtp, verifyOtp } from '../../api/onboarding';

const authLightTheme = {
  pageBg: '#f8fafc',
  text: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  label: '#475569',
  inputBg: '#ffffff',
  inputBorder: '#e2e8f0',
  inputText: '#0f172a',
  inputFocusBorder: '#4f46e5',
  inputFocusRing: 'rgba(79,70,229,0.1)',
  divider: '#e2e8f0',
  errorBg: '#fee2e2',
  errorBorder: '#fca5a5',
  errorText: '#991b1b',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  border: '#e2e8f0',
  cardBg: '#ffffff',
  cardBorder: '#e2e8f0',
  cardText: '#475569',
  cardMuted: '#64748b',
  selectedCardBg: '#f5f3ff',
  selectedCardBorder: '#4f46e5',
  selectedCardText: '#4f46e5',
  successBg: '#f0fdf4',
  successBorder: '#bbf7d0',
  successText: '#166534',
  summaryBg: '#f8fafc',
  summaryBorder: '#e2e8f0',
};

const authDarkTheme = {
  pageBg: '#020617',
  text: '#f8fafc',
  muted: '#cbd5e1',
  subtle: '#94a3b8',
  label: '#cbd5e1',
  inputBg: '#0f172a',
  inputBorder: '#334155',
  inputText: '#f8fafc',
  inputFocusBorder: '#818cf8',
  inputFocusRing: 'rgba(129,140,248,0.14)',
  divider: '#334155',
  errorBg: 'rgba(127,29,29,0.34)',
  errorBorder: '#7f1d1d',
  errorText: '#fca5a5',
  surface: '#0f172a',
  surfaceAlt: '#111827',
  border: '#334155',
  cardBg: '#111827',
  cardBorder: '#334155',
  cardText: '#e2e8f0',
  cardMuted: '#94a3b8',
  selectedCardBg: 'rgba(79,70,229,0.12)',
  selectedCardBorder: '#818cf8',
  selectedCardText: '#e0e7ff',
  successBg: 'rgba(22,101,52,0.22)',
  successBorder: '#14532d',
  successText: '#86efac',
  summaryBg: '#0f172a',
  summaryBorder: '#334155',
};

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => setIsDark(root.classList.contains('dark'));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

const getAuthTheme = (isDark) => (isDark ? authDarkTheme : authLightTheme);

function AuthField({ label, type = "text", value, onChange, placeholder, note, icon, showToggle, error, actionButton }) {
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [show, setShow] = useState(false);
  const actualType = showToggle ? (show ? "text" : "password") : type;
  const rightPadding = actionButton ? "84px" : showToggle ? "40px" : "14px";
  return (
    <div>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.label, marginBottom: 6 }}>{label}</label>}
      <div style={{ position: "relative" }}>
        {icon && <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: error ? theme.errorText : theme.subtle, display: "flex" }}><Icon name={icon} size={15} color={error ? theme.errorText : theme.subtle} /></div>}
        <input type={actualType} value={value} onChange={onChange} placeholder={placeholder}
          style={{ width: "100%", padding: `11px ${rightPadding} 11px ${icon ? '38px' : '14px'}`, border: `1.5px solid ${error ? theme.errorText : theme.inputBorder}`, borderRadius: 10, fontSize: 13, color: theme.inputText, background: theme.inputBg, outline: "none", transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box", fontFamily: "inherit" }}
          onFocus={e => { e.target.style.borderColor = error ? theme.errorText : theme.inputFocusBorder; e.target.style.boxShadow = `0 0 0 3px ${error ? (isDark ? 'rgba(127,29,29,0.2)' : 'rgba(239,68,68,0.1)') : theme.inputFocusRing}`; }}
          onBlur={e => { e.target.style.borderColor = error ? theme.errorText : theme.inputBorder; e.target.style.boxShadow = "none"; }}
        />
        {showToggle && <button type="button" onClick={() => setShow(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: theme.subtle, display: "flex", padding: 0 }}><Icon name="eye" size={15} color={theme.subtle} /></button>}
        {actionButton && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>{actionButton}</div>}
      </div>
      {error && <p style={{ fontSize: 11, color: theme.errorText, marginTop: 4, fontWeight: 500 }}>{error}</p>}
      {note && !error && <p style={{ fontSize: 11, color: theme.subtle, marginTop: 4 }}>{note}</p>}
    </div>
  );
}

function AuthPanel() {
  const features = [
    { icon: "maintenance", label: "Maintenance Billing", desc: "Track dues, record payments, generate receipts" },
    { icon: "vendors", label: "Vendor & AMC Mgmt", desc: "Manage contracts with expiry alerts" },
    { icon: "bank", label: "Bank Accounts", desc: "Secure multi-account financial overview" },
    { icon: "audit", label: "Audit Log", desc: "Complete trail of every action taken" },
  ];
  const stats = [{ value: "2,400+", label: "Societies" }, { value: "₹18Cr+", label: "Processed" }, { value: "99.9%", label: "Uptime" }];
  return (
    <div className="hidden lg:flex" style={{ width: 420, flexShrink: 0, background: "linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)", position: "relative", overflow: "hidden", flexDirection: "column", padding: "48px 40px" }}>
      <div style={{ position: "absolute", top: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -60, right: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "40%", right: 20, width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", marginBottom: 48, position: "relative" }}>
        <CloverLogo width={180} height={49} />
      </div>
      <div style={{ position: "relative", marginBottom: 40 }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 12 }}>
          Everything your society needs,{" "}
          <span style={{ background: "linear-gradient(135deg,#818cf8,#06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in one place.</span>
        </div>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7 }}>From maintenance billing to vendor AMCs - manage your housing society with confidence and clarity.</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 40, position: "relative" }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, backdropFilter: "blur(4px)" }}>
              <Icon name={f.icon} size={15} color="rgba(255,255,255,0.65)" strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)", marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "relative", display: "flex", gap: 0, marginTop: "auto", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden" }}>
        {stats.map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "14px 10px", textAlign: "center", borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SignupScreen({ onLogin }) {
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [redirectUrl, setRedirectUrl] = useState("");
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [society, setSociety] = useState({ name: "", type: "CHS", city: "", address: "", units: "", reg: "" });
  const [admin, setAdmin] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [plan, setPlan] = useState("gold_plus");
  const [isAnnual, setIsAnnual] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const consentVersion = "1.0";
  
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [phoneOtpCode, setPhoneOtpCode] = useState("");

  const clearErr = k => setFieldErrors(e => ({ ...e, [k]: "" }));

  const ss = k => e => {
    setSociety(s => ({ ...s, [k]: e.target.value }));
    clearErr(k === "name" ? "society_name" : k === "units" ? "total_units" : k === "reg" ? "registration_no" : k);
  };

  const sa = k => e => {
    setAdmin(a => ({ ...a, [k]: e.target.value }));
    clearErr(k === "name" ? "admin_name" : k === "email" ? "admin_email" : k === "phone" ? "admin_phone" : k === "confirm" ? "password_confirmation" : k);
    if (k === 'email') { setEmailVerified(false); setEmailOtpSent(false); setEmailOtpCode(""); }
    if (k === 'phone') { setPhoneVerified(false); setPhoneOtpSent(false); setPhoneOtpCode(""); }
  };

  const plans = [
    { id: "free", maxUnits: 20, name: "Free", priceMonthly: "0", priceYearly: "0", per: "/ unit", units: "Manage your society at zero cost", features: ["Unit & Payment Management", "Basic Dashboard Overview", "Notice Board (5/month)", "Single Admin User"], color: "#64748b" },
    { id: "gold", maxUnits: 100, name: "Gold", priceMonthly: "10", priceYearly: "5", per: "/ unit", units: "Professional billing, vendors & reporting", features: ["Everything in Free", "Maintenance Billing & PDF Receipts", "Vendor & AMC Management", "Amenities & Task Management"], color: "#4f46e5" },
    { id: "gold_plus", maxUnits: 300, name: "Gold+", priceMonthly: "20", priceYearly: "10", per: "/ unit", units: "Gate security, compliance & operations", features: ["Everything in Gold", "Gate & Visitor Security", "Bank Account & Finance Management", "Emergency Alert System"], color: "#10b981", popular: true },
    { id: "gold_max", maxUnits: 999999, name: "GoldMax", priceMonthly: "30", priceYearly: "15", per: "/ unit", units: "Unlimited scale. Enterprise governance.", features: ["Everything in Gold+", "Bank Statement Import", "Election & Voting Module", "Multi-Channel Emergency Alerts"], color: "#7c3aed" },
  ];

  const validate1 = () => {
    const errs = {};
    if (!society.name) errs.society_name = "Society name is required";
    if (!society.city) errs.city = "City is required";
    if (!society.units) errs.total_units = "Total units is required";

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please fix the errors below.");
      return false;
    }
    return true;
  };

  const validate2 = () => {
    const errs = {};
    if (!admin.name) errs.admin_name = "Admin name is required";
    if (!admin.email) errs.admin_email = "Email address is required";
    if (!emailVerified) errs.admin_email = "Email address must be verified";
    if (!admin.phone) errs.admin_phone = "Phone number is required";
    if (!phoneVerified) errs.admin_phone = "Phone number must be verified";
    if (!admin.password) errs.password = "Password is required";
    if (admin.password && admin.password.length < 8) errs.password = "Password must be at least 8 characters";
    if (admin.password !== admin.confirm) errs.password_confirmation = "Passwords do not match";
    if (!consentAccepted) errs.consent_accepted = "You must agree to the Privacy Policy";
    if (!ageConfirmed) errs.age_confirmed = "You must confirm your age";

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please fix the errors below.");
      return false;
    }
    return true;
  };

  const prev = () => {
    setError("");
    if (step > 1) setStep(s => s - 1);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = process.env.REACT_APP_RAZORPAY_CHECKOUT_URL || 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSendVerification = async (contact, channel) => {
    try {
      setLoading(true);
      await sendVerificationOtp(contact, channel);
      if (channel === 'mail') setEmailOtpSent(true);
      if (channel === 'sms') setPhoneOtpSent(true);
      toast.success(`${channel === 'mail' ? 'Email' : 'SMS'} OTP sent!`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (contact, code, channel) => {
    try {
      setLoading(true);
      await verifyOtp(contact, code);
      if (channel === 'mail') setEmailVerified(true);
      if (channel === 'sms') setPhoneVerified(true);
      toast.success(`${channel === 'mail' ? 'Email' : 'Phone'} verified!`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const next = async () => {
    setError("");
    setFieldErrors({});

    if (step === 1 && !validate1()) return;
    if (step === 2 && !validate2()) return;

    if (step === 2) {
      const units = parseInt(society.units || 0);
      if (units > plans.find(p => p.id === plan)?.maxUnits) {
        const bestPlan = plans.find(p => p.maxUnits >= units) || plans[plans.length - 1];
        setPlan(bestPlan.id);
      }
    }

    if (step === 3) {
      setLoading(true);
      try {
        const payload = {
          society_name: society.name,
          society_slug: society.name.toLowerCase().trim().replace(/ /g, '-').replace(/[^\w-]+/g, ''),
          society_type: society.type,
          city: society.city,
          address: society.address,
          total_units: parseInt(society.units),
          registration_no: society.reg,
          admin_name: admin.name,
          admin_email: admin.email,
          admin_phone: admin.phone,
          password: admin.password,
          password_confirmation: admin.confirm,
          consent_accepted: consentAccepted,
          age_confirmed: ageConfirmed,
          consent_version: consentVersion,
          plan_slug: plan,
          billing_cycle: 'yearly'
        };
        const response = await createSociety(payload);

        if (response.data.requires_payment) {
          const res = await loadRazorpayScript();
          if (!res) {
            setError('Razorpay SDK failed to load. Please check your connection.');
            setLoading(false);
            return;
          }

          const options = {
            key: response.data.razorpay_key,
            subscription_id: response.data.razorpay_subscription_id,
            name: "The Clover App",
            description: "Society Subscription Plan",
            handler: function (rzpResponse) {
              // Payment successful. Webhook will provision the tenant in background.
              const redirectPort = window.location.port ? `:${window.location.port}` : '';
              setRedirectUrl(`${window.location.protocol}//${payload.society_slug}.${window.location.hostname}${redirectPort}/login?registered=true&email=${encodeURIComponent(admin.email)}`);
              localStorage.setItem('registered_email', admin.email);
              setStep(4);
            },
            prefill: {
              name: admin.name,
              email: admin.email,
              contact: admin.phone
            },
            theme: {
              color: "#4f46e5"
            }
          };

          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', function (resp) {
            setError('Payment failed. Please try again.');
          });
          rzp.open();
        } else {
          const baseRedirectUrl = response.data?.redirect_url || '';
          const targetUrl = baseRedirectUrl
            ? (baseRedirectUrl.includes('?') ? `${baseRedirectUrl}&email=${encodeURIComponent(admin.email)}` : `${baseRedirectUrl}?email=${encodeURIComponent(admin.email)}`)
            : '';
          setRedirectUrl(targetUrl || response.data?.redirect_url || '');
          localStorage.setItem('registered_email', admin.email);
          setStep(4);
        }

      } catch (err) {
        if (err.errors) {
          setFieldErrors(err.errors);

          // Exhaustive jump-back logic
          const s1Fields = ['society_name', 'society_slug', 'city', 'address', 'total_units', 'registration_no'];
          const s2Fields = ['admin_name', 'admin_email', 'admin_phone', 'password', 'password_confirmation'];

          if (s1Fields.some(f => err.errors[f])) {
            setStep(1);
          } else if (s2Fields.some(f => err.errors[f])) {
            setStep(2);
          }

          setError("Validation failed. Please check the fields.");
        } else {
          setError(err.message || "Unable to create society. Please try again.");
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    setStep(s => s + 1);
  };

  const handleGoToDashboard = () => {
    localStorage.setItem('registered_email', admin.email);
    // if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    onLogin(admin.email);
    // } else {
    //   window.location.href = redirectUrl;
    // }
  };

  const STEP_LABELS = ["Society Info", "Admin Account", "Choose Plan", "All Set!"];

  return (
    <div className="flex h-screen w-full" style={{ background: theme.pageBg }}>
      <AuthPanel />

      <div className="flex-1 overflow-y-auto px-4 py-12">
        <div className="w-full mx-auto animate-fade-up" style={{ maxWidth: step === 3 ? 700 : 440 }}>

          {step !== 4 && (
            <button onClick={onLogin} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.muted, fontWeight: 600, background: "none", border: "none", cursor: "pointer", marginBottom: 28, padding: 0, fontFamily: "inherit" }}
              onMouseEnter={e => e.currentTarget.style.color = theme.text}
              onMouseLeave={e => e.currentTarget.style.color = theme.muted}>
              <Icon name="chevron_right" size={14} color="currentColor" style={{ transform: "rotate(180deg)" }} />
              Back to sign in
            </button>
          )}

          {step !== 4 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 10 }}>
                {STEP_LABELS.slice(0, 3).map((l, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", flex: i < 2 ? 1 : "none" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: step > i + 1 ? "#10b981" : step === i + 1 ? "linear-gradient(135deg,#4f46e5,#7c3aed)" : "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .3s", boxShadow: step === i + 1 ? "0 2px 8px rgba(79,70,229,0.35)" : "none" }}>
                        {step > i + 1
                          ? <Icon name="check" size={12} color="white" strokeWidth={2.5} />
                          : <span style={{ fontSize: 11, fontWeight: 800, color: step === i + 1 ? "#fff" : "#94a3b8" }}>{i + 1}</span>
                        }
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 600, color: step === i + 1 ? "#4f46e5" : step > i + 1 ? "#10b981" : "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>{l}</span>
                    </div>
                    {i < 2 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? "#10b981" : "#e2e8f0", margin: "0 6px", marginBottom: 18, transition: "background .3s" }} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: theme.errorText, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="alert" size={13} color={theme.errorText} /> {error}
            </div>
          )}

          {step === 1 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 4 }}>Register your society</div>
                <div style={{ fontSize: 13, color: theme.muted }}>Tell us about your housing society</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <AuthField label="Society Name *" icon="building" value={society.name} onChange={ss("name")} placeholder="e.g. Sunrise Heights CHS" error={fieldErrors.society_name || fieldErrors.society_slug} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.label, marginBottom: 6 }}>Society Type *</label>
                    <select value={society.type} onChange={ss("type")} style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${theme.inputBorder}`, borderRadius: 10, fontSize: 13, color: theme.inputText, background: theme.inputBg, outline: "none", fontFamily: "inherit" }}
                      onFocus={e => { e.target.style.borderColor = theme.inputFocusBorder; e.target.style.boxShadow = `0 0 0 3px ${theme.inputFocusRing}`; }}
                      onBlur={e => { e.target.style.borderColor = theme.inputBorder; e.target.style.boxShadow = "none"; }}>
                      {["Apartment Complex", "Villa / Gated Community", "Township", "Commercial Complex"].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <AuthField label="City *" icon="building" value={society.city} onChange={ss("city")} placeholder="Mumbai" error={fieldErrors.city} />
                </div>
                <AuthField label="Full Address" value={society.address} onChange={ss("address")} placeholder="Plot No., Area, City - PIN" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  <AuthField label="Total Units *" type="number" value={society.units} onChange={ss("units")} placeholder="e.g. 120" note="Enter accurately. You must contact support to change this later due to billing rules." error={fieldErrors.total_units} />
                  <AuthField label="Registration No." value={society.reg} onChange={ss("reg")} placeholder="Optional" error={fieldErrors.registration_no} />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 4 }}>Create admin account</div>
                <div style={{ fontSize: 13, color: theme.muted }}>This will be the Super Admin for your society</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <AuthField label="Full Name *" icon="user" value={admin.name} onChange={sa("name")} placeholder="Your full name" error={fieldErrors.admin_name} />
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <AuthField label="Email Address *" type="email" icon="mail" value={admin.email} onChange={sa("email")} placeholder="admin@society.in" error={fieldErrors.admin_email} 
                    actionButton={
                      emailVerified ? (
                        <span style={{ fontSize: 11, color: theme.successText, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="check" size={12} /> Verified</span>
                      ) : !emailOtpSent ? (
                        <button onClick={() => handleSendVerification(admin.email, 'mail')} disabled={loading || !admin.email} style={{ fontSize: 11, background: theme.selectedCardBg, color: theme.selectedCardText, border: "none", borderRadius: 6, padding: "5px 12px", cursor: loading || !admin.email ? "not-allowed" : "pointer", fontWeight: 700, transition: "opacity .2s", opacity: (loading || !admin.email) ? 0.6 : 1 }}>
                          Send OTP
                        </button>
                      ) : (
                        <button onClick={() => handleSendVerification(admin.email, 'mail')} disabled={loading || !admin.email} style={{ fontSize: 11, background: "none", color: theme.subtle, border: "none", cursor: loading || !admin.email ? "not-allowed" : "pointer", fontWeight: 600 }}>
                          Resend
                        </button>
                      )
                    }
                  />
                  {emailOtpSent && !emailVerified && (
                    <AuthField label="Email OTP" type="text" value={emailOtpCode} onChange={e => setEmailOtpCode(e.target.value)} placeholder="Enter 6-digit OTP" 
                      actionButton={
                        <button onClick={() => handleVerifyOtp(admin.email, emailOtpCode, 'mail')} disabled={loading || !emailOtpCode || emailOtpCode.length < 6} style={{ fontSize: 11, background: theme.selectedCardBg, color: theme.selectedCardText, border: "none", borderRadius: 6, padding: "5px 12px", cursor: loading || !emailOtpCode || emailOtpCode.length < 6 ? "not-allowed" : "pointer", fontWeight: 700, transition: "opacity .2s", opacity: (loading || !emailOtpCode || emailOtpCode.length < 6) ? 0.6 : 1 }}>
                          Verify
                        </button>
                      }
                    />
                  )}

                  <AuthField label="Phone Number *" icon="services" value={admin.phone} onChange={sa("phone")} placeholder="+91 98765 43210" error={fieldErrors.admin_phone} 
                    actionButton={
                      phoneVerified ? (
                        <span style={{ fontSize: 11, color: theme.successText, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="check" size={12} /> Verified</span>
                      ) : !phoneOtpSent ? (
                        <button onClick={() => handleSendVerification(admin.phone, 'sms')} disabled={loading || !admin.phone} style={{ fontSize: 11, background: theme.selectedCardBg, color: theme.selectedCardText, border: "none", borderRadius: 6, padding: "5px 12px", cursor: loading || !admin.phone ? "not-allowed" : "pointer", fontWeight: 700, transition: "opacity .2s", opacity: (loading || !admin.phone) ? 0.6 : 1 }}>
                          Send OTP
                        </button>
                      ) : (
                        <button onClick={() => handleSendVerification(admin.phone, 'sms')} disabled={loading || !admin.phone} style={{ fontSize: 11, background: "none", color: theme.subtle, border: "none", cursor: loading || !admin.phone ? "not-allowed" : "pointer", fontWeight: 600 }}>
                          Resend
                        </button>
                      )
                    }
                  />
                  {phoneOtpSent && !phoneVerified && (
                    <AuthField label="Phone OTP" type="text" value={phoneOtpCode} onChange={e => setPhoneOtpCode(e.target.value)} placeholder="Enter 6-digit OTP" 
                      actionButton={
                        <button onClick={() => handleVerifyOtp(admin.phone, phoneOtpCode, 'sms')} disabled={loading || !phoneOtpCode || phoneOtpCode.length < 6} style={{ fontSize: 11, background: theme.selectedCardBg, color: theme.selectedCardText, border: "none", borderRadius: 6, padding: "5px 12px", cursor: loading || !phoneOtpCode || phoneOtpCode.length < 6 ? "not-allowed" : "pointer", fontWeight: 700, transition: "opacity .2s", opacity: (loading || !phoneOtpCode || phoneOtpCode.length < 6) ? 0.6 : 1 }}>
                          Verify
                        </button>
                      }
                    />
                  )}
                </div>
                <AuthField label="Password *" icon="lock" showToggle value={admin.password} onChange={sa("password")} placeholder="Min 8 characters" error={fieldErrors.password} />
                {admin.password && (
                  <div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                      {[
                        { met: admin.password.length >= 1, color: "#ef4444" },
                        { met: admin.password.length >= 6, color: "#f59e0b" },
                        { met: admin.password.length >= 10 && /[A-Z]/.test(admin.password) && /\d/.test(admin.password), color: "#10b981" },
                      ].map((s, i) => (
                        <div key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: s.met ? s.color : "#e2e8f0", transition: "background .3s" }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>Use uppercase, numbers, and symbols for a stronger password</div>
                  </div>
                )}
                <AuthField label="Confirm Password *" icon="lock" showToggle value={admin.confirm} onChange={sa("confirm")} placeholder="Repeat password" error={fieldErrors.password_confirmation} />

                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={consentAccepted} onChange={e => { setConsentAccepted(e.target.checked); clearErr("consent_accepted"); }} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: theme.text, lineHeight: 1.4 }}>
                      I agree to the <a href="#" onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }} style={{ color: "#4f46e5", textDecoration: "none", fontWeight: 600 }}>Privacy Policy</a> and Terms of Service.
                    </span>
                  </label>
                  {fieldErrors.consent_accepted && <p style={{ fontSize: 11, color: theme.errorText, marginTop: -8, fontWeight: 500 }}>{fieldErrors.consent_accepted}</p>}

                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={ageConfirmed} onChange={e => { setAgeConfirmed(e.target.checked); clearErr("age_confirmed"); }} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: theme.text, lineHeight: 1.4 }}>
                      I confirm I am 18 years of age or older.
                    </span>
                  </label>
                  {fieldErrors.age_confirmed && <p style={{ fontSize: 11, color: theme.errorText, marginTop: -8, fontWeight: 500 }}>{fieldErrors.age_confirmed}</p>}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 4 }}>Choose your plan</div>
                <div style={{ fontSize: 13, color: theme.muted }}>Select a plan that fits your society's needs.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
                {plans.map(p => {
                  const isDisabled = parseInt(society.units || 0) > p.maxUnits;
                  return (
                    <div key={p.id} onClick={() => {
                      if (isDisabled) {
                        toast.error(`The ${p.name} plan supports up to ${p.maxUnits} units. You entered ${society.units} units. Go back to update it.`, { duration: 5000, position: 'top-center' });
                      } else {
                        setPlan(p.id);
                      }
                    }} style={{ position: "relative", borderRadius: 14, border: `2px solid ${plan === p.id ? p.color : theme.cardBorder}`, background: plan === p.id ? (isDark ? theme.selectedCardBg : `${p.color}0a`) : theme.cardBg, padding: "18px 16px", cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.4 : 1, transition: "all .2s", boxShadow: plan === p.id ? `0 4px 20px ${p.color}22` : "none" }}>
                      {p.popular && !isDisabled && (
                        <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg,${p.color},#7c3aed)`, color: "#fff", fontSize: 9, fontWeight: 800, padding: "3px 10px", borderRadius: 99, whiteSpace: "nowrap", letterSpacing: ".06em", textTransform: "uppercase" }}>
                          Most Popular
                        </div>
                      )}
                      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", color: p.color, marginBottom: 6 }}>{p.name}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 4 }}>
                        <span style={{ fontSize: 22, fontWeight: 900, color: theme.text }}>₹{p.priceYearly}</span>
                        <span style={{ fontSize: 11, color: theme.subtle }}>{p.per} / yr (Billed Annually)</span>
                      </div>
                      <div style={{ fontSize: 10, color: theme.cardMuted, marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>{p.units}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {p.features.map((feat, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11, color: theme.cardText }}>
                            <Icon name="check" size={11} color={p.color} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
                            {feat}
                          </div>
                        ))}
                      </div>
                      {plan === p.id && (
                        <div style={{ position: "absolute", top: 12, right: 12, width: 18, height: 18, borderRadius: "50%", background: p.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Icon name="check" size={10} color="white" strokeWidth={3} />
                        </div>
                      )}
                      {isDisabled && (
                        <div style={{ position: "absolute", top: 12, right: 12, fontSize: 10, fontWeight: 700, color: theme.errorText, background: theme.errorBg, padding: "3px 8px", borderRadius: 99 }}>
                          Max {p.maxUnits} units
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <div style={{ background: theme.successBg, border: `1px solid ${theme.successBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: theme.successText, display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <Icon name="shield" size={13} color={theme.successText} />
                Cancel anytime · Easily switch or upgrade plans later
              </div>
            </>
          )}

          {step === 4 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <div style={{ width: 100, height: 100, borderRadius: "50%", background: "linear-gradient(135deg,#d1fae5,#a7f3d0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg,#10b981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(16,185,129,0.4)" }}>
                    <Icon name="check" size={32} color="white" strokeWidth={2.5} />
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 8 }}>
                You're all set! 🎉
              </div>
              <div style={{ fontSize: 13, color: theme.muted, lineHeight: 1.7, marginBottom: 12 }}>
                <strong style={{ color: theme.text }}>{society.name || "Your society"}</strong> has been registered successfully.
                <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, color: "#d97706", fontSize: 12, fontWeight: 500, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Icon name="clock" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>We are securely provisioning your society database. <strong>Please wait 1-2 minutes</strong> to ensure everything is ready.</span>
                </div>
                <div style={{ marginTop: 12, padding: "12px 14px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, color: "#2563eb", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10 }}>
                  <Icon name="mail" size={18} style={{ flexShrink: 0 }} />
                  <span>You will receive an email once your society is ready to use.</span>
                </div>
              </div>
              <div style={{ background: theme.summaryBg, border: `1px solid ${theme.summaryBorder}`, borderRadius: 12, padding: "16px 20px", textAlign: "left", marginBottom: 28 }}>
                {[
                  ["Society", society.name || "-"],
                  ["Admin", admin.name || "-"],
                  ["Email", admin.email || "-"],
                  ["Plan", plans.find(p => p.id === plan)?.name],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${theme.border}`, fontSize: 12 }}>
                    <span style={{ color: theme.subtle, fontWeight: 600 }}>{k}</span>
                    <span style={{ color: theme.text, fontWeight: 700, maxWidth: 220, textAlign: "right", wordBreak: "break-word" }}>{v}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleGoToDashboard} style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", boxShadow: "0 4px 16px rgba(79,70,229,0.35)", letterSpacing: "0.01em" }}>
                Go to Dashboard →
              </button>
            </div>
          )}

          {step < 4 && (
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              {step > 1 && (
                <button onClick={prev} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 11, border: "1.5px solid #e2e8f0", cursor: loading ? "not-allowed" : "pointer", background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 700, fontFamily: "inherit", transition: "all .2s" }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = "#94a3b8"; e.currentTarget.style.color = "#0f172a"; } }}
                  onMouseLeave={e => { if (!loading) { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#64748b"; } }}>
                  Back
                </button>
              )}
              <button onClick={next} disabled={loading} style={{ flex: step > 1 ? 2 : 1, padding: "12px", borderRadius: 11, border: "none", cursor: loading ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", boxShadow: "0 4px 16px rgba(79,70,229,0.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.75 : 1 }}>
                {loading
                  ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Setting up your account…</>
                  : step === 3 ? "Complete Registration →" : `Continue →`
                }
              </button>
            </div>
          )}

          {step < 4 && (
            <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginTop: 16 }}>
              Already registered?{" "}
              <button onClick={onLogin} style={{ color: "#4f46e5", fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Sign in</button>
            </p>
          )}
        </div>
      </div>

      {showPrivacyModal && (
        <Modal title="Privacy Policy & Terms" onClose={() => setShowPrivacyModal(false)} maxWidth="md">
          <div className="space-y-4 text-sm text-ink dark:text-slate-200">
            <h4 className="font-bold text-base">The Clover App Platform - Terms & Privacy</h4>
            <p>Welcome to The Clover App Platform. By signing up and creating a society, you agree to our Terms of Service and Privacy Policy.</p>

            <h5 className="font-bold mt-4">1. Information We Collect</h5>
            <p>We collect essential information to provide society management services, including your name, email, phone number, property details, vehicle information, and visitor logs.</p>

            <h5 className="font-bold mt-4">2. How We Use Your Data</h5>
            <p>Your data is securely stored and used strictly for application features such as maintenance billing, facility booking, visitor management, and society communications.</p>

            <h5 className="font-bold mt-4">3. Data Sharing & Third Parties</h5>
            <p>We do not sell your personal data. We may share necessary data with trusted third-party service providers (like payment gateways) strictly for processing transactions, or when required by law.</p>

            <h5 className="font-bold mt-4">4. Data Security & Protection</h5>
            <p>We implement robust, industry-standard security measures including encryption and role-based access controls to protect your data against unauthorized access or disclosure.</p>

            <h5 className="font-bold mt-4">5. User Rights & Account Deletion</h5>
            <p>You have the right to access, correct, or request deletion of your personal data at any time from the settings menu. Upon deletion, all personally identifiable information will be permanently anonymized.</p>

            <h5 className="font-bold mt-4">6. Changes to this Policy</h5>
            <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes through the application or via email.</p>

            <div className="pt-4 flex justify-end">
              <Btn onClick={() => setShowPrivacyModal(false)} variant="primary">I Understand</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
